import {
  bodySlotOf,
  type Equipped,
  getCompanion,
  getItem,
  getTrail,
  type ItemTemplate,
} from '@shefira/shared';

import { puffOf, spriteOf, type TrailParticle } from '../game/trail-particles.js';
import { type AtlasFrame, DollPreview } from './doll-preview.js';

/**
 * The shop's moving pictures (ADR 0031): the character dressed in a
 * cosmetic, or a companion's beast, drawn on DOM canvases from the very
 * frames the game plays and cycled together on the rigs' idle.
 */

export interface ShopPreviewSource {
  /** The played class's body prefix in the character atlas. */
  bodyKey: string;
  frame: (name: string) => AtlasFrame | null;
  /** An image of the effects atlas, where the trails' sprites are (ADR 0073). */
  effectFrame: (name: string) => AtlasFrame | null;
  featureTints: { eyes: string; hair: string; skin: string };
  hairStyle?: string;
  body?: string;
  /** A body key (`body.fire-hound`) to its prefix in the atlas, for the companions. */
  prefixOf: (bodyKey: string) => string | undefined;
}

/** Where a beast's drawing goes in its canvas: in the middle, or feet on the ground line. */
export type BeastAnchor = 'centre' | 'feet';

/** The rigs' idle has four frames at this pace; the previews keep step with the game. */
const IDLE_FRAME_MS = 200;
const IDLE_FRAMES = 4;

/** A trail's pixels move smoothly: they are redrawn far more often than the idle steps. */
const TRAIL_FRAME_MS = 40;
/** The pace of a walk on foot, in pixels a second: a tile each quarter second. */
const WALK_PX_PER_S = 64;
/**
 * The character walks to the right, set a little right of the middle so its
 * wake has the frame's width to spread behind it: every trail's card is
 * framed alike. Its feet stand there in the 32 px frame.
 */
const TRAIL_DOLL_SHIFT = 6;
const DOLL_FEET = { x: 16 + TRAIL_DOLL_SHIFT, y: 30 };

/** The side of a piece card's preview frame (`.shop__preview`). */
const CARD_PX = 96;

/** A placeholder instance: the previews dress the doll with templates, not owned items. */
const PREVIEW_INSTANCE_ID = '00000000-0000-4000-8000-000000000000';

/** A template worn in its body slot, as the doll wants it. */
function wearing(template: ItemTemplate): Equipped {
  const slot = bodySlotOf(template);
  if (slot === undefined || template.sprite === undefined) return {};
  return {
    [slot]: {
      id: PREVIEW_INSTANCE_ID,
      templateId: template.id,
      quantity: 1,
      upgradeLevel: 0,
      bound: false,
    },
  };
}

/** The character wearing every cosmetic of a list of templates at once. */
export function dressedIn(templateIds: readonly string[]): Equipped {
  let equipped: Equipped = {};
  for (const id of templateIds) {
    const template = getItem(id);
    if (template === undefined || template.companion !== undefined) continue;
    equipped = { ...equipped, ...wearing(template) };
  }
  return equipped;
}

/**
 * Whole-pixel shift that puts a frame's opaque pixels where `anchor` wants
 * them in a `side`×`side` canvas: centred, or standing on the bottom edge with
 * one pixel of ground. A beast's drawing sits where its generator left it
 * (the horse low and to the right), so the still frame is measured once and
 * every frame moves alike.
 */
function beastShift(
  still: AtlasFrame,
  side: number,
  anchor: BeastAnchor,
): { x: number; y: number } {
  const scratch = document.createElement('canvas');
  scratch.width = side;
  scratch.height = side;
  const context = scratch.getContext('2d');
  if (context === null) return { x: 0, y: 0 };
  context.drawImage(still.image, still.x, still.y, still.width, still.height, 0, 0, side, side);
  const { data } = context.getImageData(0, 0, side, side);
  let left = side;
  let right = -1;
  let top = side;
  let bottom = -1;
  for (let py = 0; py < side; py += 1) {
    for (let px = 0; px < side; px += 1) {
      if ((data[(py * side + px) * 4 + 3] ?? 0) === 0) continue;
      left = Math.min(left, px);
      right = Math.max(right, px);
      top = Math.min(top, py);
      bottom = Math.max(bottom, py);
    }
  }
  if (right < 0) return { x: 0, y: 0 };
  const x = Math.floor((side - (right - left + 1)) / 2) - left;
  const y =
    anchor === 'centre' ? Math.floor((side - (bottom - top + 1)) / 2) - top : side - 2 - bottom;
  return { x, y };
}

/** Every preview of the open view, redrawn together on each idle frame. */
export class ShopPreviews {
  private readonly draws: (() => void)[] = [];
  private readonly smoothDraws: ((now: number) => void)[] = [];
  private ticker: number | undefined;
  private smoothTicker: number | undefined;
  private frameIndex = 0;

  constructor(private readonly source: ShopPreviewSource) {}

  /** Forget the previews of the view being replaced. */
  clear(): void {
    this.draws.length = 0;
    this.smoothDraws.length = 0;
  }

  start(): void {
    if (this.ticker !== undefined) return;
    this.ticker = window.setInterval(() => {
      this.frameIndex = (this.frameIndex + 1) % IDLE_FRAMES;
      for (const draw of this.draws) draw();
    }, IDLE_FRAME_MS);
    this.smoothTicker = window.setInterval(() => {
      const now = performance.now();
      for (const draw of this.smoothDraws) draw(now);
    }, TRAIL_FRAME_MS);
  }

  stop(): void {
    if (this.ticker === undefined) return;
    window.clearInterval(this.ticker);
    window.clearInterval(this.smoothTicker);
    this.ticker = undefined;
    this.smoothTicker = undefined;
  }

  /** The character dressed as `equipped`, centred or standing at the bottom of its canvas. */
  doll(equipped: Equipped, scale: number, centred = true): HTMLCanvasElement {
    const { source } = this;
    const doll = new DollPreview({
      bodyKey: source.bodyKey,
      frame: source.frame,
      featureTints: source.featureTints,
      hairStyle: source.hairStyle,
      body: source.body,
      scale,
      centred,
    });
    this.keep(() => {
      doll.draw(equipped, 'south', this.frameIndex);
    });
    return doll.canvas;
  }

  /** A companion's beast facing the camera; null without frames. */
  beast(template: ItemTemplate, scale: number, anchor: BeastAnchor): HTMLCanvasElement | null {
    const { source } = this;
    const companion =
      template.companion === undefined ? undefined : getCompanion(template.companion);
    const prefix = companion === undefined ? undefined : source.prefixOf(companion.bodyKey);
    const still = prefix === undefined ? null : source.frame(`${prefix}/body/idle/south/0`);
    if (companion === undefined || prefix === undefined || still === null) return null;
    const side = companion.canvas;
    const canvas = document.createElement('canvas');
    canvas.width = side * scale;
    canvas.height = side * scale;
    const context = canvas.getContext('2d');
    if (context === null) return null;
    const shift = beastShift(still, side, anchor);
    context.imageSmoothingEnabled = false;
    this.keep(() => {
      const frame = source.frame(`${prefix}/body/idle/south/${String(this.frameIndex)}`) ?? still;
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(
        frame.image,
        frame.x,
        frame.y,
        frame.width,
        frame.height,
        shift.x * scale,
        shift.y * scale,
        side * scale,
        side * scale,
      );
    });
    return canvas;
  }

  /**
   * One piece alive, as a card shows it: a cosmetic on the bare character, a
   * companion as its beast; null when the atlas has no frames for it yet.
   */
  piece(template: ItemTemplate): HTMLCanvasElement | null {
    if (template.companion !== undefined) {
      // As large as the card's 96 px frame allows: a 48 px mount at ×2, a 32 px pet at ×3.
      const side = getCompanion(template.companion)?.canvas ?? CARD_PX;
      return this.beast(template, Math.max(1, Math.floor(CARD_PX / side)), 'centre');
    }
    if (template.trail !== undefined) return this.trail(template, 3);
    const equipped = wearing(template);
    return Object.keys(equipped).length === 0 ? null : this.doll(equipped, 3);
  }

  /**
   * The bare character over its footstep trail (ADR 0073), as if walking to
   * the right: the puffs are left at its feet at the pace of a walk, and the
   * ground slides away to the left under them.
   */
  trail(template: ItemTemplate, scale: number): HTMLCanvasElement | null {
    const trail = getTrail(template.trail ?? '');
    if (trail === undefined) return null;
    const { source } = this;
    const doll = this.doll({}, scale, false);
    const canvas = document.createElement('canvas');
    canvas.className = 'doll__preview';
    canvas.width = doll.width;
    canvas.height = doll.height;
    const context = canvas.getContext('2d');
    if (context === null) return null;
    context.imageSmoothingEnabled = false;
    const particles: TrailParticle[] = [];
    const prefix = source.prefixOf(trail.sprite) ?? trail.sprite;
    let walked = 0;
    let last = performance.now();
    const draw = (now: number): void => {
      const elapsed = Math.min(200, now - last);
      last = now;
      walked += (elapsed / 1000) * WALK_PX_PER_S;
      while (walked >= trail.every) {
        walked -= trail.every;
        particles.push(...puffOf(trail, DOLL_FEET, now));
      }
      context.clearRect(0, 0, canvas.width, canvas.height);
      let kept = 0;
      for (const particle of particles) {
        // The ground slides left at the pace of the walk, as the world does
        // under a walker: the older the sprite, the further behind.
        particle.x -= (elapsed / 1000) * WALK_PX_PER_S;
        const sprite = spriteOf(particle, now);
        if (sprite === null) continue;
        particles[kept] = particle;
        kept += 1;
        const frame = source.effectFrame(`${prefix}/idle/${String(sprite.frame)}`);
        if (frame === null) continue;
        context.globalAlpha = sprite.alpha;
        context.drawImage(
          frame.image,
          frame.x,
          frame.y,
          frame.width,
          frame.height,
          (sprite.x - Math.floor(frame.width / 2)) * scale,
          (sprite.y - Math.floor(frame.height / 2)) * scale,
          frame.width * scale,
          frame.height * scale,
        );
      }
      particles.length = kept;
      context.globalAlpha = 1;
      context.drawImage(doll, TRAIL_DOLL_SHIFT * scale, 0);
    };
    draw(last);
    this.smoothDraws.push(draw);
    return canvas;
  }

  private keep(draw: () => void): void {
    draw();
    this.draws.push(draw);
  }
}
